import type { Circuit, CircuitEdge, CircuitEdgeColor } from '@multirx/shared/types/track.js';
import { TrackCellType } from '@multirx/shared/types/track.js';
import {
  cellCoordKey,
  findPathIndexAt,
  getFullCircuitBounds,
  getCorePath,
  isPathClosed,
  type CircuitPathBounds,
} from '@multirx/shared/game/circuitEdit.js';
import { getGeometryEditorBounds } from '@multirx/shared/game/circuitGeometryEdit.js';
import {
  createCanvasPropDrawTarget,
  drawCircuitProps,
} from '@multirx/shared/game/circuitPropDraw.js';
import { createVehicleSpriteDrawer } from './circuitVehicleSprites.js';
import { iterGridRectCells, normalizeGridRect } from '@multirx/shared/game/circuitPropEdit.js';
import { getDecorCheckerCss } from '@multirx/shared/game/circuitDecorStyle.js';
import { isInfrastructureCell } from '@multirx/shared/game/circuitOccupancy.js';
import { drawCircuitCellEdges } from '@multirx/shared/game/circuitEdgeDraw.js';
import {
  drawDamierCellFill,
  getCircuitCellStyle,
  isDamierSurface,
  isSolidCircuitSurface,
  isStyleableCircuitCell,
  pickCircuitEdgeAtLocalPoint,
  SURFACE_FILL_CSS,
} from '@multirx/shared/game/circuitSurfaceStyle.js';

export type CircuitEditorMode =
  'trace' | 'erase' | 'paint' | 'joker-lap' | 'start-lane' | 'surface' | 'decor';

export interface CircuitEditorCanvasOptions {
  cellSize?: number;
  padding?: number;
  mode?: CircuitEditorMode;
  /** Coordonnées (x,y) des cases en erreur à surligner en rouge. */
  errorCells?: ReadonlyArray<{ x: number; y: number }>;
  /** Cases cliquables en mode Tracer (aperçu). */
  placeableCells?: ReadonlyArray<{ x: number; y: number }>;
  /** Aperçu rectangle gradins / panneau (coin 1 → coin 2). */
  propPreviewRect?: { x1: number; y1: number; x2: number; y2: number } | null;
}

const DEFAULT_CELL_SIZE = 28;
const DEFAULT_PADDING = 16;

const CELL_FILL: Record<number, string> = {
  [TrackCellType.STRAIGHT]: 'rgba(199, 107, 120, 0.55)',
  [TrackCellType.TURN]: 'rgba(234, 88, 12, 0.6)',
  [TrackCellType.START]: 'rgba(250, 204, 21, 0.65)',
};

const ERROR_FILL = 'rgba(239, 68, 68, 0.82)';
const ERROR_STROKE = '#fecaca';
const EMPTY_FILL = 'rgba(30, 41, 59, 0.85)';
const PLACEABLE_FILL = 'rgba(34, 197, 94, 0.22)';
const PLACEABLE_STROKE = 'rgba(74, 222, 128, 0.75)';
const JOKER_LAP_FILL = 'rgba(168, 85, 247, 0.7)';
const JOKER_LAP_STROKE = '#e9d5ff';
const JOKER_ENTRY_FILL = 'rgba(168, 85, 247, 0.35)';
const START_LANE_FILL = 'rgba(251, 191, 36, 0.75)';
const START_LANE_STROKE = '#fef3c7';
const START_LANE_JOIN_FILL = 'rgba(251, 191, 36, 0.35)';
const DECOR_STROKE = 'rgba(34, 197, 94, 0.55)';
const PROP_PREVIEW_FILL = 'rgba(56, 189, 248, 0.22)';
const PROP_PREVIEW_STROKE = 'rgba(56, 189, 248, 0.9)';
const DIRECTION_ARROW_FILL = 'rgba(248, 250, 252, 0.95)';
const DIRECTION_ARROW_START_FILL = '#fffbeb';
const DIRECTION_ARROW_START_STROKE = '#f59e0b';
const DIRECTION_JOKER_ARROW_FILL = 'rgba(250, 245, 255, 0.95)';
const DIRECTION_START_LANE_ARROW_FILL = 'rgba(254, 243, 199, 0.95)';

type PxPoint = { x: number; y: number };

function cellCenter(
  cell: { x: number; y: number },
  cellPx: (cell: { x: number; y: number }) => { x: number; y: number },
  cellSize: number
): PxPoint {
  const p = cellPx(cell);
  return { x: p.x + cellSize / 2, y: p.y + cellSize / 2 };
}

/** Flèche au milieu d’un segment (sens de parcours). */
function drawDirectionArrow(
  ctx: CanvasRenderingContext2D,
  from: PxPoint,
  to: PxPoint,
  size: number,
  fill: string,
  stroke?: string
): void {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === 0) return;

  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const angle = Math.atan2(dy, dx);

  ctx.save();
  ctx.translate(mx, my);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size * 0.65, size * 0.58);
  ctx.lineTo(-size * 0.65, -size * 0.58);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = Math.max(1, size * 0.12);
    ctx.stroke();
  }
  ctx.restore();
}

function drawPathDirectionArrows(
  ctx: CanvasRenderingContext2D,
  cells: ReadonlyArray<{ x: number; y: number }>,
  cellPx: (cell: { x: number; y: number }) => { x: number; y: number },
  cellSize: number,
  options: {
    closed?: boolean;
    arrowSize?: number;
    fill?: string;
    stroke?: string;
    /** Flèche de départ plus visible sur le 1er segment. */
    highlightStart?: boolean;
  } = {}
): void {
  if (cells.length < 2) return;

  const arrowSize = options.arrowSize ?? cellSize * 0.2;
  const fill = options.fill ?? DIRECTION_ARROW_FILL;
  const closed = options.closed ?? false;
  const segmentCount = closed ? cells.length : cells.length - 1;

  for (let i = 0; i < segmentCount; i++) {
    const from = cellCenter(cells[i], cellPx, cellSize);
    const to = cellCenter(cells[(i + 1) % cells.length], cellPx, cellSize);
    const isStart = options.highlightStart && i === 0;
    drawDirectionArrow(
      ctx,
      from,
      to,
      isStart ? arrowSize * 1.35 : arrowSize,
      isStart ? DIRECTION_ARROW_START_FILL : fill,
      isStart ? DIRECTION_ARROW_START_STROKE : options.stroke
    );
  }
}

function getOptions(options?: CircuitEditorCanvasOptions): Required<CircuitEditorCanvasOptions> {
  return {
    cellSize: options?.cellSize ?? DEFAULT_CELL_SIZE,
    padding: options?.padding ?? DEFAULT_PADDING,
    mode: options?.mode ?? 'paint',
    errorCells: options?.errorCells ?? [],
    placeableCells: options?.placeableCells ?? [],
    propPreviewRect: options?.propPreviewRect ?? null,
  };
}

function resolveSurfaceFill(circuit: Circuit, x: number, y: number, defaultFill: string): string {
  const custom = getCircuitCellStyle(circuit, x, y)?.fill;
  if (custom && isSolidCircuitSurface(custom)) return SURFACE_FILL_CSS[custom];
  return defaultFill;
}

function paintCellSurfaceFill(
  ctx: CanvasRenderingContext2D,
  circuit: Circuit,
  gx: number,
  gy: number,
  px: number,
  py: number,
  cellSize: number,
  defaultFill: string
): void {
  const custom = getCircuitCellStyle(circuit, gx, gy)?.fill;
  const pad = 1;
  const inner = cellSize - pad * 2;
  const ix = px + pad;
  const iy = py + pad;
  if (custom && isDamierSurface(custom)) {
    drawDamierCellFill(
      (x, y, w, h, color) => {
        ctx.fillStyle = String(color);
        ctx.fillRect(x, y, w, h);
      },
      ix,
      iy,
      inner,
      gx,
      gy
    );
    return;
  }
  ctx.fillStyle = custom && isSolidCircuitSurface(custom) ? SURFACE_FILL_CSS[custom] : defaultFill;
  ctx.fillRect(ix, iy, inner, inner);
}

function drawCellSurfaceEdges(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  cellSize: number,
  edges: Partial<Record<CircuitEdge, CircuitEdgeColor>>
): void {
  drawCircuitCellEdges(
    {
      fillRect: (x, y, w, h, color) => {
        ctx.fillStyle = String(color);
        ctx.fillRect(x, y, w, h);
      },
    },
    px,
    py,
    cellSize,
    edges
  );
}

export function getEditorCanvasBounds(
  circuit: Circuit,
  _mode: CircuitEditorMode
): CircuitPathBounds {
  if (circuit.path.length === 0) {
    return getGeometryEditorBounds(circuit);
  }
  const full = getFullCircuitBounds(circuit, 1);
  if (full) return full;
  return getGeometryEditorBounds(circuit);
}

export function getCircuitEditorCanvasSize(
  circuit: Circuit,
  options?: CircuitEditorCanvasOptions
): { width: number; height: number } {
  const { cellSize, padding, mode } = getOptions(options);
  const bounds = getEditorCanvasBounds(circuit, mode);
  return {
    width: bounds.widthCells * cellSize + padding * 2,
    height: bounds.heightCells * cellSize + padding * 2,
  };
}

export function drawCircuitEditorCanvas(
  canvas: HTMLCanvasElement,
  circuit: Circuit,
  options?: CircuitEditorCanvasOptions
): void {
  const { cellSize, padding, mode, errorCells, placeableCells, propPreviewRect } =
    getOptions(options);
  const bounds = getEditorCanvasBounds(circuit, mode);
  const size = getCircuitEditorCanvasSize(circuit, options);
  canvas.width = size.width;
  canvas.height = size.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const ox = padding;
  const oy = padding;
  const path = circuit.path;
  const errorKeys = new Set(errorCells.map((cell) => cellCoordKey(cell.x, cell.y)));
  const placeableKeys = new Set(placeableCells.map((cell) => cellCoordKey(cell.x, cell.y)));

  const cellPx = (cell: { x: number; y: number }) => ({
    x: ox + (cell.x - bounds.minX) * cellSize,
    y: oy + (cell.y - bounds.minY) * cellSize,
  });

  ctx.fillStyle = '#1e293b';
  ctx.fillRect(0, 0, size.width, size.height);

  if (mode === 'trace' || mode === 'joker-lap' || mode === 'start-lane' || mode === 'decor') {
    for (let gy = bounds.minY; gy <= bounds.maxY; gy++) {
      for (let gx = bounds.minX; gx <= bounds.maxX; gx++) {
        const { x: px, y: py } = cellPx({ x: gx, y: gy });
        const key = cellCoordKey(gx, gy);
        const isPlaceable = placeableKeys.has(key);
        ctx.fillStyle = isPlaceable ? PLACEABLE_FILL : EMPTY_FILL;
        ctx.fillRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
        if (isPlaceable) {
          ctx.strokeStyle = PLACEABLE_STROKE;
          ctx.lineWidth = 2;
          ctx.strokeRect(px + 1.5, py + 1.5, cellSize - 3, cellSize - 3);
        }
      }
    }
  }

  for (const decor of circuit.decorCells ?? []) {
    const { x: px, y: py } = cellPx(decor);
    ctx.fillStyle = getDecorCheckerCss(decor.x, decor.y);
    ctx.fillRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
    ctx.strokeStyle = DECOR_STROKE;
    ctx.lineWidth = 1;
    ctx.strokeRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
  }

  for (const [key, style] of Object.entries(circuit.cellStyles ?? {})) {
    if (!style.fill) continue;
    const [gx, gy] = key.split(',').map(Number);
    if (isInfrastructureCell(circuit, gx, gy)) continue;
    const { x: px, y: py } = cellPx({ x: gx, y: gy });
    if (isSolidCircuitSurface(style.fill)) {
      ctx.fillStyle = SURFACE_FILL_CSS[style.fill];
      ctx.fillRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
    } else {
      drawDamierCellFill(
        (x, y, w, h, color) => {
          ctx.fillStyle = String(color);
          ctx.fillRect(x, y, w, h);
        },
        px + 1,
        py + 1,
        cellSize - 2,
        gx,
        gy
      );
    }
    ctx.strokeStyle = DECOR_STROKE;
    ctx.lineWidth = 1;
    ctx.strokeRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
  }

  const propTarget = {
    ...createCanvasPropDrawTarget(ctx),
    drawSprite: createVehicleSpriteDrawer(ctx),
  };
  drawCircuitProps(propTarget, circuit, cellPx, cellSize);

  if (propPreviewRect) {
    const rect = normalizeGridRect(
      propPreviewRect.x1,
      propPreviewRect.y1,
      propPreviewRect.x2,
      propPreviewRect.y2
    );
    for (const cell of iterGridRectCells(rect)) {
      const { x: px, y: py } = cellPx(cell);
      ctx.fillStyle = PROP_PREVIEW_FILL;
      ctx.fillRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
      ctx.strokeStyle = PROP_PREVIEW_STROKE;
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(px + 1.5, py + 1.5, cellSize - 3, cellSize - 3);
      ctx.setLineDash([]);
    }
  }

  ctx.strokeStyle = 'rgba(148, 163, 184, 0.25)';
  ctx.lineWidth = 1;
  for (let x = 0; x <= bounds.widthCells; x++) {
    const px = ox + x * cellSize;
    ctx.beginPath();
    ctx.moveTo(px, oy);
    ctx.lineTo(px, oy + bounds.heightCells * cellSize);
    ctx.stroke();
  }
  for (let y = 0; y <= bounds.heightCells; y++) {
    const py = oy + y * cellSize;
    ctx.beginPath();
    ctx.moveTo(ox, py);
    ctx.lineTo(ox + bounds.widthCells * cellSize, py);
    ctx.stroke();
  }

  const drawn = new Set<string>();
  for (let i = 0; i < path.length; i++) {
    const cell = path[i];
    const key = cellCoordKey(cell.x, cell.y);
    if (drawn.has(key)) continue;
    drawn.add(key);

    const isError = errorKeys.has(key);
    const isJokerEntry = circuit.jokerLap?.entryPathIndex === i;
    const isJokerExit = circuit.jokerLap?.exitPathIndex === i;
    const isStartLaneJoin = circuit.startLane?.joinPathIndex === i;
    const typeFill = isError
      ? ERROR_FILL
      : isJokerEntry || isJokerExit
        ? JOKER_ENTRY_FILL
        : isStartLaneJoin
          ? START_LANE_JOIN_FILL
          : (CELL_FILL[cell.type] ?? 'rgba(100, 116, 139, 0.4)');
    const customFill = getCircuitCellStyle(circuit, cell.x, cell.y)?.fill;
    const { x: px, y: py } = cellPx(cell);
    if (!isError && customFill && isDamierSurface(customFill)) {
      paintCellSurfaceFill(ctx, circuit, cell.x, cell.y, px, py, cellSize, typeFill);
    } else {
      const fill = isError ? typeFill : resolveSurfaceFill(circuit, cell.x, cell.y, typeFill);
      ctx.fillStyle = fill;
      ctx.fillRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
    }

    ctx.strokeStyle = isError ? ERROR_STROKE : 'rgba(15, 23, 42, 0.65)';
    ctx.lineWidth = isError ? 2 : 1.5;
    ctx.strokeRect(px + 1, py + 1, cellSize - 2, cellSize - 2);

    const pathStyle = getCircuitCellStyle(circuit, cell.x, cell.y);
    if (pathStyle?.edges) {
      drawCellSurfaceEdges(ctx, px, py, cellSize, pathStyle.edges);
    }

    if (!isError) {
      ctx.fillStyle = '#0f172a';
      ctx.font = `bold ${Math.max(10, cellSize * 0.34)}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const label = isJokerEntry
        ? '↪'
        : isJokerExit
          ? '↩'
          : isStartLaneJoin
            ? '⊕'
            : cell.type === TrackCellType.START
              ? 'D'
              : cell.type === TrackCellType.TURN
                ? 'V'
                : mode === 'trace'
                  ? String(i + 1)
                  : '—';
      ctx.fillText(label, px + cellSize / 2, py + cellSize / 2);
    }
  }

  const jokerCells = circuit.jokerLap?.cells ?? [];
  if (jokerCells.length > 0) {
    for (let ji = 0; ji < jokerCells.length; ji++) {
      const cell = jokerCells[ji];
      const key = cellCoordKey(cell.x, cell.y);
      if (drawn.has(`joker:${key}`)) continue;
      drawn.add(`joker:${key}`);
      const { x: px, y: py } = cellPx(cell);
      paintCellSurfaceFill(ctx, circuit, cell.x, cell.y, px, py, cellSize, JOKER_LAP_FILL);
      ctx.strokeStyle = JOKER_LAP_STROKE;
      ctx.lineWidth = 2;
      ctx.strokeRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
      const jokerStyle = getCircuitCellStyle(circuit, cell.x, cell.y);
      if (jokerStyle?.edges) {
        drawCellSurfaceEdges(ctx, px, py, cellSize, jokerStyle.edges);
      }
      ctx.fillStyle = '#faf5ff';
      ctx.font = `bold ${Math.max(10, cellSize * 0.34)}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('J', px + cellSize / 2, py + cellSize / 2);
    }
    const firstPx = cellPx(jokerCells[0]);
    let jpx = firstPx.x + cellSize / 2;
    let jpy = firstPx.y + cellSize / 2;
    ctx.beginPath();
    ctx.moveTo(jpx, jpy);
    for (let ji = 1; ji < jokerCells.length; ji++) {
      const p = cellPx(jokerCells[ji]);
      jpx = p.x + cellSize / 2;
      jpy = p.y + cellSize / 2;
      ctx.lineTo(jpx, jpy);
    }
    ctx.strokeStyle = 'rgba(192, 132, 252, 0.9)';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
    drawPathDirectionArrows(ctx, jokerCells, cellPx, cellSize, {
      fill: DIRECTION_JOKER_ARROW_FILL,
      arrowSize: cellSize * 0.18,
    });
  }

  const startLaneCells = circuit.startLane?.cells ?? [];
  if (startLaneCells.length > 0) {
    for (let si = 0; si < startLaneCells.length; si++) {
      const cell = startLaneCells[si];
      const key = cellCoordKey(cell.x, cell.y);
      if (drawn.has(`start:${key}`)) continue;
      drawn.add(`start:${key}`);
      const { x: px, y: py } = cellPx(cell);
      paintCellSurfaceFill(ctx, circuit, cell.x, cell.y, px, py, cellSize, START_LANE_FILL);
      ctx.strokeStyle = START_LANE_STROKE;
      ctx.lineWidth = 2;
      ctx.strokeRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
      const laneStyle = getCircuitCellStyle(circuit, cell.x, cell.y);
      if (laneStyle?.edges) {
        drawCellSurfaceEdges(ctx, px, py, cellSize, laneStyle.edges);
      }
      ctx.fillStyle = '#422006';
      ctx.font = `bold ${Math.max(10, cellSize * 0.34)}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(si + 1), px + cellSize / 2, py + cellSize / 2);
    }
    const joinIdx = circuit.startLane?.joinPathIndex ?? -1;
    if (joinIdx >= 0 && joinIdx < path.length) {
      const joinPx = cellPx(path[joinIdx]);
      const firstPx = cellPx(startLaneCells[0]);
      ctx.beginPath();
      ctx.moveTo(joinPx.x + cellSize / 2, joinPx.y + cellSize / 2);
      ctx.lineTo(firstPx.x + cellSize / 2, firstPx.y + cellSize / 2);
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.9)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    if (startLaneCells.length > 1) {
      const a = cellPx(startLaneCells[0]);
      ctx.beginPath();
      ctx.moveTo(a.x + cellSize / 2, a.y + cellSize / 2);
      for (let si = 1; si < startLaneCells.length; si++) {
        const p = cellPx(startLaneCells[si]);
        ctx.lineTo(p.x + cellSize / 2, p.y + cellSize / 2);
      }
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.75)';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
      drawPathDirectionArrows(ctx, startLaneCells, cellPx, cellSize, {
        fill: DIRECTION_START_LANE_ARROW_FILL,
        arrowSize: cellSize * 0.18,
      });
    }
  }

  if (path.length > 0) {
    const firstPx = cellPx(path[0]);
    let px = firstPx.x + cellSize / 2;
    let py = firstPx.y + cellSize / 2;
    ctx.beginPath();
    ctx.moveTo(px, py);
    for (let i = 1; i < path.length; i++) {
      const p = cellPx(path[i]);
      px = p.x + cellSize / 2;
      py = p.y + cellSize / 2;
      ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.85)';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    const core = getCorePath(path);
    if (core.length >= 2) {
      drawPathDirectionArrows(ctx, core, cellPx, cellSize, {
        closed: isPathClosed(path),
        highlightStart: true,
      });
    }
  }

  for (const err of errorCells) {
    const { x: px, y: py } = cellPx(err);
    ctx.fillStyle = ERROR_FILL;
    ctx.fillRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
    ctx.strokeStyle = ERROR_STROKE;
    ctx.lineWidth = 2.5;
    ctx.strokeRect(px + 1, py + 1, cellSize - 2, cellSize - 2);
    ctx.fillStyle = '#fff';
    ctx.font = `bold ${Math.max(10, cellSize * 0.38)}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('!', px + cellSize / 2, py + cellSize / 2);
  }
}

export function circuitEditorGridHitTest(
  canvas: HTMLCanvasElement,
  circuit: Circuit,
  clientX: number,
  clientY: number,
  options?: CircuitEditorCanvasOptions
): { x: number; y: number } | null {
  const { cellSize, padding, mode } = getOptions(options);
  const bounds = getEditorCanvasBounds(circuit, mode);
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const x = (clientX - rect.left) * scaleX;
  const y = (clientY - rect.top) * scaleY;
  const gridX = Math.floor((x - padding) / cellSize) + bounds.minX;
  const gridY = Math.floor((y - padding) / cellSize) + bounds.minY;
  if (gridX < bounds.minX || gridY < bounds.minY || gridX > bounds.maxX || gridY > bounds.maxY) {
    return null;
  }
  return { x: gridX, y: gridY };
}

export function circuitEditorHitTest(
  canvas: HTMLCanvasElement,
  circuit: Circuit,
  clientX: number,
  clientY: number,
  options?: CircuitEditorCanvasOptions
): number | null {
  const { mode } = getOptions(options);
  const grid = circuitEditorGridHitTest(canvas, circuit, clientX, clientY, options);
  if (!grid) return null;
  const bounds = getEditorCanvasBounds(circuit, mode);
  if (
    grid.x < bounds.minX ||
    grid.y < bounds.minY ||
    grid.x > bounds.maxX ||
    grid.y > bounds.maxY
  ) {
    return null;
  }
  return findPathIndexAt(circuit, grid.x, grid.y);
}

export function circuitEditorSurfaceHitTest(
  canvas: HTMLCanvasElement,
  circuit: Circuit,
  clientX: number,
  clientY: number,
  target: 'fill' | 'edge',
  options?: CircuitEditorCanvasOptions
): { x: number; y: number; edge?: CircuitEdge } | null {
  const { cellSize, padding, mode } = getOptions(options);
  const grid = circuitEditorGridHitTest(canvas, circuit, clientX, clientY, options);
  if (!grid || !isStyleableCircuitCell(circuit, grid.x, grid.y)) return null;
  if (target === 'fill') return grid;

  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const x = (clientX - rect.left) * scaleX;
  const y = (clientY - rect.top) * scaleY;
  const bounds = getEditorCanvasBounds(circuit, mode);
  const localX = x - padding - (grid.x - bounds.minX) * cellSize;
  const localY = y - padding - (grid.y - bounds.minY) * cellSize;
  return {
    x: grid.x,
    y: grid.y,
    edge: pickCircuitEdgeAtLocalPoint(localX, localY, cellSize),
  };
}
